'use client';

import {
  ChevronDownIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { Editor } from '@tiptap/react';
import { Button } from '@ui/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/core/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/core/tooltip';
import { useMediaQuery } from '@ui/lib/use-media-query';
import { cn } from '@ui/lib/utils';
import {
  Bold,
  CheckSquare,
  ChevronLeft,
  Code,
  Expand,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Text,
  Underline,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { BlurredDivider } from '../../BlurredDivider';

interface FloatingToolbarProps {
  editor: Editor;
  onPopOut?: () => void;
  isExpanded?: boolean;
  focusModeIcon?: React.ReactNode;
  focusModeLabel?: string;
  minimizeButtonLabel?: string;
  minimizeButtonIcon?: React.ReactNode;
  onMinimize?: () => void;
}

interface ShortcutProps {
  shortcut: string;
  isMobile: boolean;
  isMac: boolean;
}

const Shortcut: React.FC<ShortcutProps> = ({ shortcut, isMobile, isMac }) => {
  if (isMobile || !shortcut) return null;

  // Format the command part (⌘ for Mac, Ctrl for Windows)
  const cmdPart = isMac ? '⌘' : 'Ctrl';

  // Extract the key part (the character after "Ctrl+" or last character)
  const keyPart = shortcut.includes('+') ? shortcut.split('+')[1] : shortcut;

  return (
    <div className="ml-auto flex items-center justify-center">
      <div className="text-xs border border-slate-300/85 rounded px-1.5 py-0.5 bg-white shadow-xs flex items-center gap-0.5 font-bold">
        <span>{cmdPart}</span>
        <svg
          className="h-2.5 w-2.5 text-slate-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
        </svg>
        <span>{keyPart}</span>
      </div>
    </div>
  );
};

export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  editor,
  onPopOut,
  isExpanded = false,
  focusModeIcon = <Expand className="h-4 w-4" />,
  focusModeLabel = 'Focus Mode',
  minimizeButtonLabel = 'Exit Focus Mode',
  minimizeButtonIcon = <ChevronLeft className="h-4 w-4" />,
  onMinimize,
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const isSmall = useMediaQuery('(max-width: 640px)');
  const isMac =
    typeof navigator !== 'undefined'
      ? navigator.platform.toUpperCase().indexOf('MAC') >= 0
      : false;

  // Monitor the actual width of the toolbar container
  useEffect(() => {
    if (!toolbarRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      setIsNarrow(width < 512);
    });

    resizeObserver.observe(toolbarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!editor) return null;

  // Text style items
  const textStyleItems = [
    {
      icon: <Text className="h-4 w-4" />,
      title: 'Body',
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive('paragraph'),
      shortcut: '',
    },
    {
      icon: <Heading1 className="h-4 w-4" />,
      title: 'Title',
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive('heading', { level: 1 }),
      shortcut: 'Ctrl+1',
    },
    {
      icon: <Heading2 className="h-4 w-4" />,
      title: 'Heading',
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive('heading', { level: 2 }),
      shortcut: 'Ctrl+2',
    },
    {
      icon: <Heading3 className="h-4 w-4" />,
      title: 'Subheading',
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive('heading', { level: 3 }),
      shortcut: 'Ctrl+3',
    },
  ];

  // Keep these visible even in narrow mode - PRIORITY ITEMS
  const narrowModeVisibleItems = [
    {
      icon: <Bold className="h-4 w-4" />,
      title: 'Bold',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
      shortcut: 'B',
    },
    {
      icon: <Italic className="h-4 w-4" />,
      title: 'Italic',
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
      shortcut: 'I',
    },
    {
      icon: <Underline className="h-4 w-4" />,
      title: 'Underline',
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: () => editor.isActive('underline'),
      shortcut: 'U',
    },
    {
      icon: <List className="h-4 w-4" />,
      title: 'Bullet List',
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
      shortcut: '',
    },
  ];

  // Other item groups
  const remainingBasicFormattingItems = [
    {
      icon: <LinkIcon className="h-4 w-4" />,
      title: 'Link',
      action: () => {
        const url = window.prompt('URL');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      },
      isActive: () => editor.isActive('link'),
      shortcut: 'K',
    },
  ];

  const listItems = [
    {
      icon: <ListOrdered className="h-4 w-4" />,
      title: 'Numbered List',
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList'),
      shortcut: '',
    },
    {
      icon: <CheckSquare className="h-4 w-4" />,
      title: 'Task List',
      action: () => editor.chain().focus().toggleTaskList().run(),
      isActive: () => editor.isActive('taskList'),
      shortcut: '',
    },
  ];

  const moreItems = [
    {
      icon: <Strikethrough className="h-4 w-4" />,
      title: 'Strikethrough',
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
      shortcut: 'X',
    },
    {
      icon: <Quote className="h-4 w-4" />,
      title: 'Quote',
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.isActive('blockquote'),
      shortcut: '',
    },
    {
      icon: <Code className="h-4 w-4" />,
      title: 'Code Block',
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.isActive('codeBlock'),
      shortcut: '',
    },
  ];

  // Determine what to show based on container width and screen size
  let visibleToolbarItems = [];
  let responsiveMoreItems = [];

  if (isSmall || isNarrow) {
    // Even on small screens or narrow containers, keep basic formatting visible
    visibleToolbarItems = narrowModeVisibleItems;
    responsiveMoreItems = [
      ...remainingBasicFormattingItems,
      { divider: true },
      ...listItems,
      { divider: true },
      ...moreItems,
    ];
  } else {
    // On wider containers, show more items directly
    visibleToolbarItems = [
      ...narrowModeVisibleItems,
      ...remainingBasicFormattingItems,
      ...listItems,
    ];
    responsiveMoreItems = moreItems;
  }

  return (
    <div
      ref={toolbarRef}
      className={cn(
        'bg-white px-[1px] py-1.5 pt-2 flex items-center overflow-x-auto gap-x-2',
        // Only apply border, rounded corners and right padding in focus mode
        isExpanded
          ? 'border-slate-200/65 border rounded-[16px] shadow-sm pr-2'
          : ''
      )}
    >
      {/* Text Styles Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-bold ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-8 gap-1.5 px-0 sm:px-2 hover:bg-slate-50/80 transition duration-200 ease-in-out cursor-pointer">
          <DocumentTextIcon className="h-4 w-4" />
          {<span className="hidden">Text Style</span>}
          <ChevronDownIcon className="h-3 w-3 opacity-50" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="min-w-[200px] w-[200px]"
          style={{ minWidth: '200px' }}
        >
          {textStyleItems.map((item, index) => (
            <DropdownMenuItem
              key={index}
              onClick={item.action}
              className={cn(
                'cursor-pointer flex items-center py-1.5',
                item.isActive() && 'bg-slate-100 font-medium'
              )}
            >
              {item.icon}
              <span className="ml-2">{item.title}</span>
              {item.shortcut && !isNarrow && (
                <div className="ml-auto flex items-center justify-center">
                  <div className="text-xs font-medium border border-slate-300/85 rounded px-1.5 py-0.5 bg-white shadow-xs flex items-center gap-1">
                    <span>{isMac ? '⌘' : 'Ctrl'}</span>
                    <svg
                      className="h-2.5 w-2.5 text-slate-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                    </svg>
                    <span>{item.shortcut}</span>
                  </div>
                </div>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div
        className={cn(
          'h-6 w-px bg-slate-200 mx-2',
          (isSmall || isNarrow) && 'hidden'
        )}
      />

      {/* Always visible formatting items */}
      {visibleToolbarItems.length > 0 && (
        <div
          className={cn('flex', isSmall || isNarrow ? 'gap-x-2' : 'gap-x-1.5')}
        >
          {visibleToolbarItems.map((item, index) => (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="default"
                  onClick={item.action}
                  className={cn(
                    'h-8 w-8 p-0',
                    item.isActive() && 'bg-slate-100',
                    (isSmall || isNarrow) &&
                      'ring-1 ring-slate-200/45 shadow-sm hover:bg-slate-50/80 transition duration-200 ease-in-out rounded-lg'
                  )}
                >
                  {item.icon}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="font-medium">
                  {item.title}{' '}
                  {item.shortcut && !isNarrow && (
                    <span className="inline-flex items-center ml-1">
                      <span>{isMac ? '⌘' : 'Ctrl'}</span>
                      <svg
                        className="h-2.5 w-2.5 mx-0.5 text-slate-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                      </svg>
                      <span>{item.shortcut}</span>
                    </span>
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}

      {/* More Menu - always visible */}
      <div
        className={cn(
          'h-6 w-px bg-slate-200 mx-2',
          (isSmall || isNarrow) && 'hidden'
        )}
      />

      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-bold ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-8 w-8 p-0 hover:bg-slate-50/80 transition duration-200 ease-in-out cursor-pointer">
          <EllipsisHorizontalIcon className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px]">
          {responsiveMoreItems.map((item, index) =>
            'divider' in item ? (
              <BlurredDivider key={`divider-${index}`} className="mb-3" />
            ) : (
              <DropdownMenuItem
                key={index}
                onClick={item.action}
                className={cn(
                  'cursor-pointer flex items-center py-1.5',
                  item.isActive && item.isActive() && 'bg-slate-100 font-medium'
                )}
              >
                {item.icon}
                <span className="ml-2">{item.title}</span>
                {item.shortcut && !isNarrow && (
                  <div className="ml-auto flex items-center justify-center">
                    <div className="text-xs font-medium border border-slate-300/85 rounded px-1.5 py-0.5 bg-white shadow-xs flex items-center gap-1">
                      <span>{isMac ? '⌘' : 'Ctrl'}</span>
                      <svg
                        className="h-2.5 w-2.5 text-slate-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                      </svg>
                      <span>{item.shortcut}</span>
                    </div>
                  </div>
                )}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Focus Mode button when not expanded */}
      {!isExpanded && onPopOut && (
        <div className="ml-auto">
          <div
            onClick={onPopOut}
            className={cn(
              'relative border-2 border-transparent bg-white text-slate-800 rounded-lg inline-flex items-center justify-start text-left text-xs font-medium cursor-pointer outline-none',
              'ring-1 ring-slate-200/45 shadow-sm hover:bg-slate-50/80 transition duration-200 ease-in-out rounded-lg'
            )}
          >
            {focusModeIcon}
            <span className="text-xs font-bold hidden">{focusModeLabel}</span>
          </div>
        </div>
      )}
    </div>
  );
};
